import { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ReactComponent as CloseIcon } from 'assets/close-icon.svg';
import i18nContext from 'components/i18n-context';
import { useApplicationContext } from 'contexts/ApplicationContext';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import { CheckBox } from 'uikit/CheckBox/CheckBox';
import './PopUpManageOperatorScheme.scss';

// Permission (authority) groups, matched by suffix. 'other' must stay last as the catch-all.
const PERMISSION_GROUPS = ['read', 'create', 'update', 'other'];

// INDIVIDUAL_ACCOUNT_DETAILS_READ -> "Individual account details read"
const humanizeAuthority = (authority) => {
  const text = authority.toLowerCase().replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const PopUpManageOperatorScheme = ({ isLoading, operatorData, handleClose, handleSave }) => {
  const i18n = useContext(i18nContext);
  const { constants } = useApplicationContext();
  const isEditMode = !!operatorData;

  // Get operator roles from context
  const operatorRoles = constants?.operatorRoles || [];

  // Prepare role options for dropdown
  const roleOptions = operatorRoles.map((role) => ({
    key: role,
    value: i18n.getMessage(`operators.role.${role}`) || role
  }));

  // Available granular permissions come from application constants (operatorAuthorities)
  const availableAuthorities = useMemo(() => constants?.operatorAuthorities || [], [constants]);

  // Group permissions by action suffix (READ/CREATE/UPDATE); everything else falls into 'other'
  const groupedAuthorities = useMemo(() => {
    const groups = PERMISSION_GROUPS.reduce((acc, name) => ({ ...acc, [name]: [] }), {});
    availableAuthorities.forEach((authority) => {
      const group =
        PERMISSION_GROUPS.find((name) => name !== 'other' && authority.endsWith(`_${name.toUpperCase()}`)) || 'other';
      groups[group].push(authority);
    });
    return groups;
  }, [availableAuthorities]);

  const initialValues = {
    login: operatorData?.login || '',
    password: '',
    email: operatorData?.email || '',
    name: operatorData?.name || '',
    role: operatorData?.role || '',
    enabled: operatorData?.enabled !== undefined ? operatorData.enabled : true,
    authorities: operatorData?.authorities || []
  };

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues,
    validationSchema: Yup.object().shape({
      login: Yup.string()
        .required(i18n.getMessage('operators.modal.validation.required'))
        .max(25, i18n.getMessage('operators.modal.validation.maxLength', { max: 25 })),
      password: isEditMode
        ? Yup.string().max(250, i18n.getMessage('operators.modal.validation.maxLength', { max: 250 }))
        : Yup.string()
            .required(i18n.getMessage('operators.modal.validation.required'))
            .min(7, i18n.getMessage('operators.modal.validation.minLength', { min: 7 }))
            .max(250, i18n.getMessage('operators.modal.validation.maxLength', { max: 250 })),
      email: Yup.string()
        .required(i18n.getMessage('operators.modal.validation.required'))
        .email(i18n.getMessage('operators.modal.validation.invalidEmail'))
        .max(50, i18n.getMessage('operators.modal.validation.maxLength', { max: 50 })),
      name: Yup.string().max(50, i18n.getMessage('operators.modal.validation.maxLength', { max: 50 })),
      role: Yup.string().required(i18n.getMessage('operators.modal.validation.required'))
    }),
    onSubmit: async (values) => {
      const dataToSend = {
        ...values,
        // Include id for update
        ...(isEditMode && { id: operatorData.id }),
        // Don't send empty password on update
        ...(isEditMode && !values.password && { password: undefined })
      };

      // Remove undefined values
      Object.keys(dataToSend).forEach((key) => {
        if (dataToSend[key] === undefined) {
          delete dataToSend[key];
        }
      });

      await handleSave(dataToSend);
    }
  });

  const { values, handleSubmit, errors, handleChange, setFieldValue } = form;

  const toggleAuthority = (authority) => {
    const current = values.authorities || [];
    const next = current.includes(authority) ? current.filter((item) => item !== authority) : [...current, authority];
    setFieldValue('authorities', next);
  };

  return (
    <div className='popup-manage-operator-container'>
      <div className='popup-manage-operator-header'>
        <h4>
          {isEditMode
            ? i18n.getMessage('operators.modal.title.update')
            : i18n.getMessage('operators.modal.title.create')}
        </h4>
        <CloseIcon className='popup-manage-operator-close-icon' onClick={handleClose} />
      </div>

      <form className='popup-manage-operator-form' onSubmit={handleSubmit}>
        <div className='popup-manage-operator-form-row'>
          <Input
            className='popup-manage-operator-input'
            id='login'
            name='login'
            label={i18n.getMessage('operators.modal.label.login')}
            placeholder={i18n.getMessage('operators.modal.placeholder.login')}
            value={values.login}
            onChange={handleChange}
            error={errors?.login}
            disabled={isLoading || isEditMode} // Login cannot be edited
            isRequired
            autoComplete='off'
          />

          <Input
            className='popup-manage-operator-input'
            id='password'
            name='password'
            type='password'
            label={i18n.getMessage('operators.modal.label.password')}
            placeholder={
              isEditMode
                ? i18n.getMessage('operators.modal.placeholder.passwordUpdate')
                : i18n.getMessage('operators.modal.placeholder.password')
            }
            value={values.password}
            onChange={handleChange}
            error={errors?.password}
            disabled={isLoading}
            isRequired={!isEditMode}
            autoComplete='new-password'
          />
        </div>

        <div className='popup-manage-operator-form-row'>
          <Input
            className='popup-manage-operator-input'
            id='email'
            name='email'
            type='email'
            label={i18n.getMessage('operators.modal.label.email')}
            placeholder={i18n.getMessage('operators.modal.placeholder.email')}
            value={values.email}
            onChange={handleChange}
            error={errors?.email}
            disabled={isLoading}
            isRequired
            autoComplete='off'
          />

          <Input
            className='popup-manage-operator-input'
            id='name'
            name='name'
            label={i18n.getMessage('operators.modal.label.name')}
            placeholder={i18n.getMessage('operators.modal.placeholder.name')}
            value={values.name}
            onChange={handleChange}
            error={errors?.name}
            disabled={isLoading}
          />
        </div>

        <div className='popup-manage-operator-form-row'>
          <InputDropDown
            className='popup-manage-operator-input'
            name='role'
            label={i18n.getMessage('operators.modal.label.role')}
            placeholder={i18n.getMessage('operators.modal.placeholder.role')}
            value={values.role}
            options={roleOptions}
            onChange={(name, value) => setFieldValue(name, value)}
            error={errors?.role}
            isDisabled={isLoading}
            isRequired
          />

          <div className='popup-manage-operator-checkbox-wrapper'>
            <CheckBox
              onChange={() => setFieldValue('enabled', !values.enabled)}
              isActive={values.enabled}
              showCheckBox={true}
              value={i18n.getMessage('operators.modal.checkbox.enabledText')}
            />
          </div>
        </div>

        <div className='popup-manage-operator-permissions'>
          <span className='popup-manage-operator-permissions-label'>
            {i18n.getMessage('operators.modal.label.permissions')}
          </span>
          <div className='popup-manage-operator-permissions-groups'>
            {PERMISSION_GROUPS.filter((group) => groupedAuthorities[group]?.length > 0).map((group) => (
              <div className='popup-manage-operator-permissions-group' key={group}>
                <h5 className='popup-manage-operator-permissions-group-title'>
                  {i18n.getMessage(`operators.permissions.group.${group}`)}
                </h5>
                <div className='popup-manage-operator-permissions-list'>
                  {groupedAuthorities[group].map((authority) => (
                    <CheckBox
                      key={authority}
                      className='popup-manage-operator-permission'
                      isActive={values.authorities?.includes(authority)}
                      showCheckBox={true}
                      isReverseView={true}
                      value={humanizeAuthority(authority)}
                      onChange={() => toggleAuthority(authority)}
                      disabled={isLoading}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className='popup-manage-operator-actions'>
          <Button className='popup-manage-operator-button' type='outline' onClick={handleClose} isDisabled={isLoading}>
            {i18n.getMessage('operators.modal.button.cancel')}
          </Button>
          <Button className='popup-manage-operator-button' roleType='submit' type='primary' isDisabled={isLoading}>
            {isEditMode
              ? i18n.getMessage('operators.modal.button.update')
              : i18n.getMessage('operators.modal.button.create')}
          </Button>
        </div>
      </form>
    </div>
  );
};

PopUpManageOperatorScheme.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  operatorData: PropTypes.shape({
    id: PropTypes.number,
    login: PropTypes.string,
    email: PropTypes.string,
    name: PropTypes.string,
    role: PropTypes.string,
    authorities: PropTypes.arrayOf(PropTypes.string),
    enabled: PropTypes.bool
  }),
  handleClose: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired
};

export default PopUpManageOperatorScheme;
